import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { generatorAPI, projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import UpgradeModal from '../components/UpgradeModal';
import AiDisclosure from '../components/AiDisclosure';
import SceneCard from '../components/SceneCard';
import { exportStoryboardAsPDF } from '../lib/pdfExport';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Sparkles, Zap, FileText, Layout, Shuffle, Target, Users, 
  TrendingUp, Copy, Check, Download, Save, Loader2, ArrowLeft,
  Sun, Moon, Globe, ChevronRight, Package, Shield, Flame,
  Video, ExternalLink, FileDown, CheckCircle2, AlertCircle, Info
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 }
};

export default function GeneratorPage() {
  const { t, language, setLanguage, languages } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user, usage, isAdmin, canGenerate, refreshUsage } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState('hooks');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  
  const [productInput, setProductInput] = useState({
    product_url: '',
    product_name: '',
    product_description: '',
    target_audience: '',
    key_benefit: '',
    platform: 'tiktok',
    language: language
  });

  const [results, setResults] = useState({
    hooks: [],
    scripts: [],
    storyboard: [],
    variations: [],
    creatorScript: null,
    competitorAnalysis: null,
    performancePrediction: null
  });

  const [competitorName, setCompetitorName] = useState('');
  const [adContentForPrediction, setAdContentForPrediction] = useState('');

  // Load project if ID provided
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (projectId) {
      loadProject(projectId);
    }
  }, [searchParams]);

  // Update language in productInput when it changes
  useEffect(() => {
    setProductInput(prev => ({ ...prev, language }));
  }, [language]);

  const loadProject = async (projectId) => {
    try {
      const response = await projectsAPI.getOne(projectId);
      const project = response.data;
      setProductInput(project.product_input);
      setResults({
        hooks: project.hooks || [],
        scripts: project.scripts || [],
        storyboard: project.storyboard || [],
        variations: project.variations || [],
        creatorScript: project.creator_scripts?.[0] || null,
        competitorAnalysis: null,
        performancePrediction: project.performance_prediction || null
      });
    } catch (error) {
      toast.error('Failed to load project');
    }
  };

  const handleInputChange = (field, value) => {
    setProductInput(prev => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return productInput.product_name && 
           productInput.product_description && 
           productInput.target_audience && 
           productInput.key_benefit &&
           productInput.platform;
  };

  // Check if user can generate (admin always can, others check usage)
  const userCanGenerate = isAdmin || canGenerate;

  const handleGenerate = async (type) => {
    // Check usage limits (skip for predictor as it doesn't count)
    if (type !== 'predictor' && !isAdmin && !canGenerate) {
      setShowUpgradeModal(true);
      return;
    }

    if (!isFormValid() && type !== 'competitor' && type !== 'predictor') {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    try {
      let response;
      
      switch (type) {
        case 'hooks':
          response = await generatorAPI.generateHooks(productInput);
          setResults(prev => ({ ...prev, hooks: response.data.hooks }));
          toast.success('Hooks generated successfully!');
          break;
          
        case 'scripts':
          response = await generatorAPI.generateScripts(productInput);
          setResults(prev => ({ ...prev, scripts: response.data.scripts }));
          toast.success('Scripts generated successfully!');
          break;
          
        case 'storyboard':
          response = await generatorAPI.generateStoryboard(productInput);
          setResults(prev => ({ ...prev, storyboard: response.data.scenes }));
          toast.success('Storyboard generated successfully!');
          break;
          
        case 'variations':
          response = await generatorAPI.generateVariations(productInput);
          setResults(prev => ({ ...prev, variations: response.data.variations }));
          toast.success('Variations generated successfully!');
          break;
          
        case 'creator':
          response = await generatorAPI.generateCreatorScript(productInput);
          setResults(prev => ({ ...prev, creatorScript: response.data }));
          toast.success('Creator script generated successfully!');
          break;
          
        case 'competitor':
          if (!competitorName) {
            toast.error('Please enter a competitor name');
            setLoading(false);
            return;
          }
          response = await generatorAPI.analyzeCompetitor(competitorName, language);
          setResults(prev => ({ ...prev, competitorAnalysis: response.data }));
          toast.success('Competitor analysis complete!');
          break;
          
        case 'predictor':
          if (!adContentForPrediction) {
            toast.error('Please enter ad content to analyze');
            setLoading(false);
            return;
          }
          response = await generatorAPI.predictPerformance(adContentForPrediction, language);
          setResults(prev => ({ ...prev, performancePrediction: response.data }));
          toast.success('Performance prediction complete!');
          break;
          
        default:
          break;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Generation failed';
      // Check if it's a usage limit error
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        toast.error(message);
      }
      // Refresh usage after generation attempt
      refreshUsage();
    } finally {
      setLoading(false);
      // Refresh usage after successful generation
      refreshUsage();
    }
  };

  const handleCopy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(null), 2000);
  };

  const handleExportStoryboardPDF = async () => {
    if (!results.storyboard || results.storyboard.length === 0) {
      toast.error('No storyboard to export');
      return;
    }
    
    setPdfLoading(true);
    try {
      const campaignName = productInput.product_name || 'Untitled Campaign';
      await exportStoryboardAsPDF(results.storyboard, campaignName, productInput.platform);
      toast.success('PDF изтеглен успешно!');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSaveProject = async () => {
    if (!isFormValid()) {
      toast.error('Please fill in product details first');
      return;
    }
    
    try {
      const projectName = productInput.product_name || 'Untitled Project';
      const response = await projectsAPI.create(projectName, productInput);
      
      // Update with results
      await projectsAPI.update(response.data.project_id, {
        hooks: results.hooks,
        scripts: results.scripts,
        storyboard: results.storyboard,
        variations: results.variations,
        creator_scripts: results.creatorScript ? [results.creatorScript] : [],
        performance_prediction: results.performancePrediction
      });
      
      toast.success('Project saved!');
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleExport = (type) => {
    let content = '';
    let filename = '';
    
    switch (type) {
      case 'hooks':
        content = results.hooks.map(h => `[${h.type}] ${h.text}`).join('\n\n');
        filename = 'hooks.txt';
        break;
      case 'scripts':
        content = results.scripts.map(s => s.full_script).join('\n\n---\n\n');
        filename = 'scripts.txt';
        break;
      case 'storyboard':
        content = results.storyboard.map(s => 
          `Scene ${s.scene_number}: ${s.title}\n${s.description}\nCamera: ${s.camera_angle}\nDuration: ${s.duration}`
        ).join('\n\n');
        filename = 'storyboard.txt';
        break;
      case 'variations':
        content = results.variations.map(v => v.full_ad).join('\n\n---\n\n');
        filename = 'variations.txt';
        break;
      default:
        return;
    }

    // EU AI Act Article 50 §2 — machine-readable AI provenance header.
    // Prepended to every TXT export so downstream tools can detect AI origin
    // via simple grep of Key: Value pairs on comment lines starting with "# ".
    const aiHeader = [
      '# ─────────────────────────────────────────',
      '# AI-GENERATED CONTENT',
      '# Provider: AIgnyx (https://aignyx.com)',
      '# Model: OpenAI GPT-5.2',
      `# Type: ${type}`,
      `# Generated: ${new Date().toISOString()}`,
      '# EU AI Act Article 50 notice: https://aignyx.com/privacy#ai',
      '# ─────────────────────────────────────────',
      '',
      ''
    ].join('\n');

    // Prefix with UTF-8 BOM so Cyrillic/Unicode content renders correctly on
    // Windows Notepad, Android editors, and legacy tools that misdetect encoding.
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + aiHeader + content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported successfully!');
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'tiktok':
        return <div className="w-6 h-6 rounded-full platform-tiktok flex items-center justify-center text-white text-xs font-bold">T</div>;
      case 'instagram':
        return <div className="w-6 h-6 rounded-full platform-instagram flex items-center justify-center text-white text-xs font-bold">I</div>;
      case 'youtube':
        return <div className="w-6 h-6 rounded-full platform-youtube flex items-center justify-center text-white text-xs font-bold">Y</div>;
      default:
        return null;
    }
  };

  const tabs = [
    { id: 'hooks', icon: Zap, label: t('generator.hooks') },
    { id: 'scripts', icon: FileText, label: t('generator.scripts') },
    { id: 'storyboard', icon: Layout, label: t('generator.storyboard') },
    { id: 'variations', icon: Shuffle, label: t('generator.variations') },
    { id: 'competitor', icon: Target, label: t('generator.competitor') },
    { id: 'creator', icon: Users, label: t('generator.creatorScript') },
    { id: 'predictor', icon: TrendingUp, label: t('generator.predictor') },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden" data-testid="generator-page">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Upgrade Modal */}
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-heading text-base sm:text-xl font-bold truncate">{t('generator.title')}</h1>
              {/* Admin Badge */}
              {isAdmin && (
                <Badge className="bg-gradient-to-r from-amber-700 to-orange-700 text-white shrink-0 hidden sm:inline-flex">
                  <Shield className="w-3 h-3 mr-1" />
                  Admin
                </Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-4 shrink-0">
            {/* Usage Indicator (non-admin only) */}
            {!isAdmin && usage && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/50 text-sm">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className={!userCanGenerate ? 'text-destructive' : 'text-muted-foreground'}>
                  {usage.subscription_plan === 'free_trial' 
                    ? `${usage.free_trial_remaining} free left`
                    : `${usage.generations_remaining} left`
                  }
                </span>
              </div>
            )}

            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="gen-language-selector">
                  <Globe className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)}>
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme */}
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {/* Save — icon-only on mobile, icon+label on sm+ */}
            <Button
              onClick={handleSaveProject}
              variant="outline"
              size="icon"
              className="sm:hidden"
              data-testid="save-project-btn-mobile"
              aria-label={t('generator.save')}
            >
              <Save className="w-4 h-4" />
            </Button>
            <Button
              onClick={handleSaveProject}
              variant="outline"
              className="hidden sm:inline-flex"
              data-testid="save-project-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {t('generator.save')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Product Information Form - Always Visible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="dashboard-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold">Product Information</h2>
                <p className="text-sm text-muted-foreground">Enter your product details to generate ads</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Product Name */}
              <div className="space-y-2">
                <Label htmlFor="product_name">{t('generator.productName')} <span className="text-destructive">*</span></Label>
                <Input 
                  id="product_name"
                  value={productInput.product_name}
                  onChange={(e) => handleInputChange('product_name', e.target.value)}
                  placeholder="e.g. Glow Serum Pro"
                  className="h-11"
                  data-testid="product-name-input"
                />
              </div>

              {/* Target Audience */}
              <div className="space-y-2">
                <Label htmlFor="target_audience">{t('generator.targetAudience')} <span className="text-destructive">*</span></Label>
                <Input 
                  id="target_audience"
                  value={productInput.target_audience}
                  onChange={(e) => handleInputChange('target_audience', e.target.value)}
                  placeholder="e.g. Women 25-45 interested in skincare"
                  className="h-11"
                  data-testid="target-audience-input"
                />
              </div>

              {/* Platform */}
              <div className="space-y-2">
                <Label>{t('generator.platform')} <span className="text-destructive">*</span></Label>
                <Select 
                  value={productInput.platform}
                  onValueChange={(value) => handleInputChange('platform', value)}
                >
                  <SelectTrigger className="h-11" data-testid="platform-select">
                    <SelectValue placeholder={t('generator.selectPlatform')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tiktok">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full platform-tiktok" />
                        TikTok
                      </div>
                    </SelectItem>
                    <SelectItem value="instagram">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full platform-instagram" />
                        Instagram Reels
                      </div>
                    </SelectItem>
                    <SelectItem value="youtube">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full platform-youtube" />
                        YouTube Shorts
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Product Description - Full Width */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="product_description">{t('generator.productDesc')} <span className="text-destructive">*</span></Label>
                <Textarea 
                  id="product_description"
                  value={productInput.product_description}
                  onChange={(e) => handleInputChange('product_description', e.target.value)}
                  placeholder="Describe your product in detail. What makes it special? What problem does it solve?"
                  className="min-h-[80px] resize-none"
                  data-testid="product-desc-input"
                />
              </div>

              {/* Key Benefit */}
              <div className="space-y-2">
                <Label htmlFor="key_benefit">{t('generator.keyBenefit')} <span className="text-destructive">*</span></Label>
                <Input 
                  id="key_benefit"
                  value={productInput.key_benefit}
                  onChange={(e) => handleInputChange('key_benefit', e.target.value)}
                  placeholder="e.g. Reduces wrinkles by 50% in 2 weeks"
                  className="h-11"
                  data-testid="key-benefit-input"
                />
              </div>
            </div>

            {/* Form Status */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-4 border-t border-border">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                {!userCanGenerate ? (
                  <>
                    <AlertCircle className="w-4 h-4 text-destructive shrink-0" aria-hidden="true" />
                    <span className="text-sm text-destructive">No generations remaining - </span>
                    <Link to="/billing" className="text-sm text-primary underline underline-offset-2 hover:no-underline font-medium">Upgrade now</Link>
                  </>
                ) : isFormValid() ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-700 dark:text-green-400 shrink-0" aria-hidden="true" />
                    <span className="text-sm text-green-700 dark:text-green-400 font-medium">
                      {isAdmin ? 'Admin Mode - Unlimited generations' : 'Ready to generate'}
                    </span>
                  </>
                ) : (
                  <>
                    <Info className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
                    <span className="text-sm text-muted-foreground">Fill in all required fields to enable generation</span>
                  </>
                )}
              </div>
              <Badge variant="outline" className="capitalize shrink-0">
                {productInput.platform}
              </Badge>
            </div>
          </div>
        </motion.div>

        {/* Generator Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Tab Navigation — native overflow-x for reliable mobile scrolling */}
          <div className="border-b border-border mb-6 -mx-4 sm:-mx-0">
            <div
              className="flex gap-1 pb-2 overflow-x-auto no-scrollbar px-4 sm:px-0"
              data-testid="generator-tabs"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap shrink-0 transition-colors text-sm ${
                    activeTab === tab.id 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-secondary text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4 shrink-0" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* EU AI Act Article 50 — persistent AI content disclosure */}
          <div className="mb-4">
            <AiDisclosure variant="banner" />
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {/* Hooks Tab */}
            {activeTab === 'hooks' && (
              <motion.div
                key="hooks"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={fadeUp}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-semibold">{t('generator.hooks')}</h3>
                    <p className="text-sm text-muted-foreground">Generate 5 hooks with viral potential in different styles</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleGenerate('hooks')} 
                      disabled={loading || !isFormValid() || !userCanGenerate} 
                      data-testid="generate-hooks-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                      {loading ? t('generator.generating') : t('generator.generate')}
                    </Button>
                    {results.hooks.length > 0 && (
                      <Button variant="outline" onClick={() => handleExport('hooks')}>
                        <Download className="w-4 h-4 mr-2" />
                        {t('generator.export')}
                      </Button>
                    )}
                  </div>
                </div>

                {results.hooks.length === 0 ? (
                  <div className="text-center py-16 bg-card rounded-2xl border border-border">
                    <Zap className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground">Generate hooks with viral potential for your product</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Curiosity, Problem-Solution, Shocking Fact, Storytelling, Transformation</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {results.hooks.map((hook, index) => (
                      <motion.div
                        key={hook.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="dashboard-card group"
                        data-testid={`hook-${hook.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <Badge variant="secondary" className="mb-2 capitalize">
                              {hook.type.replace('_', ' ')}
                            </Badge>
                            <p className="text-lg">{hook.text}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleCopy(hook.text, hook.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            {copied === hook.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Scripts Tab */}
            {activeTab === 'scripts' && (
              <motion.div
                key="scripts"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={fadeUp}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-semibold">{t('generator.scripts')}</h3>
                    <p className="text-sm text-muted-foreground">Generate 3 complete UGC video scripts</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleGenerate('scripts')} 
                      disabled={loading || !isFormValid() || !userCanGenerate} 
                      data-testid="generate-scripts-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                      {loading ? t('generator.generating') : t('generator.generate')}
                    </Button>
                    {results.scripts.length > 0 && (
                      <Button variant="outline" onClick={() => handleExport('scripts')}>
                        <Download className="w-4 h-4 mr-2" />
                        {t('generator.export')}
                      </Button>
                    )}
                  </div>
                </div>

                {results.scripts.length === 0 ? (
                  <div className="text-center py-16 bg-card rounded-2xl border border-border">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground">Generate UGC video scripts</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Includes hook, demo, benefits, emotional trigger, and CTA</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {results.scripts.map((script, index) => (
                      <motion.div
                        key={script.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="dashboard-card"
                        data-testid={`script-${script.id}`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            {getPlatformIcon(script.platform)}
                            <span className="font-semibold capitalize">{script.platform} Script</span>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleCopy(script.full_script, script.id)}
                          >
                            {copied === script.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            {t('generator.copy')}
                          </Button>
                        </div>
                        
                        <div className="space-y-4 text-sm">
                          <div>
                            <span className="text-primary font-medium">Hook:</span>
                            <p className="mt-1">{script.intro_hook}</p>
                          </div>
                          <div>
                            <span className="text-primary font-medium">Product Demo:</span>
                            <p className="mt-1">{script.product_demo}</p>
                          </div>
                          <div>
                            <span className="text-primary font-medium">Benefits:</span>
                            <p className="mt-1">{script.benefit_explanation}</p>
                          </div>
                          <div>
                            <span className="text-primary font-medium">Emotional Trigger:</span>
                            <p className="mt-1">{script.emotional_trigger}</p>
                          </div>
                          <div>
                            <span className="text-primary font-medium">Call to Action:</span>
                            <p className="mt-1">{script.call_to_action}</p>
                          </div>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-muted-foreground text-sm font-mono">{script.full_script}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Storyboard Tab */}
            {activeTab === 'storyboard' && (
              <motion.div
                key="storyboard"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={fadeUp}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-semibold">{t('generator.storyboard')}</h3>
                    <p className="text-sm text-muted-foreground">Generate a 5-scene visual storyboard</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      onClick={() => handleGenerate('storyboard')} 
                      disabled={loading || !isFormValid() || !userCanGenerate} 
                      data-testid="generate-storyboard-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Layout className="w-4 h-4 mr-2" />}
                      {loading ? t('generator.generating') : t('generator.generate')}
                    </Button>
                    {results.storyboard.length > 0 && (
                      <>
                        <Button variant="outline" onClick={() => handleExport('storyboard')}>
                          <Download className="w-4 h-4 mr-2" />
                          {t('generator.export')}
                        </Button>
                        <Button 
                          variant="default"
                          className="bg-primary/90 hover:bg-primary"
                          onClick={handleExportStoryboardPDF}
                          disabled={pdfLoading}
                          data-testid="export-pdf-btn"
                        >
                          {pdfLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Генериране на PDF...
                            </>
                          ) : (
                            <>
                              <FileDown className="w-4 h-4 mr-2" />
                              Изтегли PDF
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {results.storyboard.length === 0 ? (
                  <div className="text-center py-16 bg-card rounded-2xl border border-border">
                    <Layout className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground">Generate visual storyboard</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Hook, Problem, Solution, Social Proof, CTA scenes</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {results.storyboard.map((scene, index) => (
                      <SceneCard
                        key={`scene-${scene.scene_number}-${index}`}
                        scene={scene}
                        index={index}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Variations Tab */}
            {activeTab === 'variations' && (
              <motion.div
                key="variations"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={fadeUp}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-semibold">{t('generator.variations')}</h3>
                    <p className="text-sm text-muted-foreground">Generate 5 ad variations for A/B testing</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleGenerate('variations')} 
                      disabled={loading || !isFormValid() || !userCanGenerate} 
                      data-testid="generate-variations-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shuffle className="w-4 h-4 mr-2" />}
                      {loading ? t('generator.generating') : t('generator.generate')}
                    </Button>
                    {results.variations.length > 0 && (
                      <Button variant="outline" onClick={() => handleExport('variations')}>
                        <Download className="w-4 h-4 mr-2" />
                        {t('generator.export')}
                      </Button>
                    )}
                  </div>
                </div>

                {results.variations.length === 0 ? (
                  <div className="text-center py-16 bg-card rounded-2xl border border-border">
                    <Shuffle className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground">Generate multiple ad variations</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Different hooks, emotional angles, CTAs, and tones</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {results.variations.map((variation, index) => (
                      <motion.div
                        key={variation.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="dashboard-card group"
                        data-testid={`variation-${variation.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex gap-2">
                            <Badge variant="outline" className="capitalize">{variation.emotional_angle}</Badge>
                            <Badge variant="secondary" className="capitalize">{variation.tone}</Badge>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleCopy(variation.full_ad, variation.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            {copied === variation.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                        <p className="text-sm mb-3"><strong>Hook:</strong> {variation.hook}</p>
                        <p className="text-sm mb-3"><strong>CTA:</strong> {variation.cta}</p>
                        <p className="text-muted-foreground text-sm font-mono">{variation.full_ad}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Competitor Analysis Tab */}
            {activeTab === 'competitor' && (
              <motion.div
                key="competitor"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={fadeUp}
                className="space-y-4"
              >
                <div>
                  <h3 className="font-heading text-lg font-semibold">{t('generator.competitor')}</h3>
                  <p className="text-sm text-muted-foreground">Analyze competitor ad strategies</p>
                </div>

                <div className="flex gap-4 mb-6">
                  <Input 
                    value={competitorName}
                    onChange={(e) => setCompetitorName(e.target.value)}
                    placeholder="Enter competitor brand name..."
                    className="max-w-md h-11"
                    data-testid="competitor-name-input"
                  />
                  <Button 
                    onClick={() => handleGenerate('competitor')} 
                    disabled={loading || !competitorName} 
                    data-testid="analyze-competitor-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                    Analyze
                  </Button>
                </div>

                {!results.competitorAnalysis ? (
                  <div className="text-center py-16 bg-card rounded-2xl border border-border">
                    <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground">Enter a competitor name to analyze their ad strategy</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="dashboard-card">
                      <h4 className="font-semibold mb-3">Common Ad Angles</h4>
                      <ul className="space-y-2">
                        {results.competitorAnalysis.common_ad_angles.map((angle, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5" />
                            {angle}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="dashboard-card">
                      <h4 className="font-semibold mb-3">Marketing Messaging</h4>
                      <ul className="space-y-2">
                        {results.competitorAnalysis.marketing_messaging.map((msg, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5" />
                            {msg}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="dashboard-card">
                      <h4 className="font-semibold mb-3">Counter Hooks</h4>
                      <ul className="space-y-2">
                        {results.competitorAnalysis.counter_hooks.map((hook, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <ChevronRight className="w-4 h-4 text-accent mt-0.5" />
                            {hook}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="dashboard-card">
                      <h4 className="font-semibold mb-3">Differentiation Strategies</h4>
                      <ul className="space-y-2">
                        {results.competitorAnalysis.differentiation_strategies.map((strategy, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <ChevronRight className="w-4 h-4 text-green-500 mt-0.5" />
                            {strategy}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Creator Script Tab */}
            {activeTab === 'creator' && (
              <motion.div
                key="creator"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={fadeUp}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-semibold">{t('generator.creatorScript')}</h3>
                    <p className="text-sm text-muted-foreground">Generate script for UGC creators with instructions</p>
                  </div>
                  <Button 
                    onClick={() => handleGenerate('creator')} 
                    disabled={loading || !isFormValid()} 
                    data-testid="generate-creator-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                    {loading ? t('generator.generating') : t('generator.generate')}
                  </Button>
                </div>

                {!results.creatorScript ? (
                  <div className="text-center py-16 bg-card rounded-2xl border border-border">
                    <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground">Generate script for UGC creators</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Includes dialogue, camera instructions, and pacing</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="dashboard-card">
                      <h4 className="font-semibold mb-3">Spoken Dialogue</h4>
                      <p className="text-sm font-mono bg-secondary/50 p-4 rounded-lg">{results.creatorScript.spoken_dialogue}</p>
                    </div>
                    <div className="dashboard-card">
                      <h4 className="font-semibold mb-3">Camera Instructions</h4>
                      <p className="text-sm">{results.creatorScript.camera_instructions}</p>
                    </div>
                    <div className="dashboard-card">
                      <h4 className="font-semibold mb-3">Pacing Notes</h4>
                      <p className="text-sm">{results.creatorScript.pacing_notes}</p>
                    </div>
                    <div className="dashboard-card">
                      <h4 className="font-semibold mb-3">Scene Structure</h4>
                      <p className="text-sm">{results.creatorScript.scene_structure}</p>
                    </div>
                    <div className="dashboard-card">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold">Full Creator Script</h4>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleCopy(results.creatorScript.full_script, 'creator')}
                        >
                          {copied === 'creator' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          {t('generator.copy')}
                        </Button>
                      </div>
                      <p className="text-sm font-mono bg-secondary/50 p-4 rounded-lg whitespace-pre-wrap">{results.creatorScript.full_script}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Performance Predictor Tab */}
            {activeTab === 'predictor' && (
              <motion.div
                key="predictor"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={fadeUp}
                className="space-y-4"
              >
                <div>
                  <h3 className="font-heading text-lg font-semibold">{t('generator.predictor')}</h3>
                  <p className="text-sm text-muted-foreground">Analyze your ad content and get performance predictions</p>
                </div>

                <div className="space-y-4 mb-6">
                  <Textarea 
                    value={adContentForPrediction}
                    onChange={(e) => setAdContentForPrediction(e.target.value)}
                    placeholder="Paste your ad content here to analyze..."
                    className="min-h-[120px] resize-none"
                    data-testid="prediction-content-input"
                  />
                  <Button 
                    onClick={() => handleGenerate('predictor')} 
                    disabled={loading || !adContentForPrediction} 
                    data-testid="predict-performance-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                    Analyze Performance
                  </Button>
                </div>

                {!results.performancePrediction ? (
                  <div className="text-center py-16 bg-card rounded-2xl border border-border">
                    <TrendingUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground">Paste your ad content above to get predictions</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="dashboard-card text-center">
                        <h4 className="text-sm text-muted-foreground mb-2">Hook Strength</h4>
                        <div className="text-3xl font-heading font-bold text-primary">
                          {results.performancePrediction.hook_strength}
                        </div>
                        <Progress value={results.performancePrediction.hook_strength} className="h-2 mt-2" />
                      </div>
                      <div className="dashboard-card text-center">
                        <h4 className="text-sm text-muted-foreground mb-2">Emotional Triggers</h4>
                        <div className="text-3xl font-heading font-bold text-accent">
                          {results.performancePrediction.emotional_triggers}
                        </div>
                        <Progress value={results.performancePrediction.emotional_triggers} className="h-2 mt-2" />
                      </div>
                      <div className="dashboard-card text-center">
                        <h4 className="text-sm text-muted-foreground mb-2">Message Clarity</h4>
                        <div className="text-3xl font-heading font-bold text-green-700 dark:text-green-400">
                          {results.performancePrediction.message_clarity}
                        </div>
                        <Progress value={results.performancePrediction.message_clarity} className="h-2 mt-2" />
                      </div>
                      <div className="dashboard-card text-center bg-primary/10">
                        <h4 className="text-sm text-muted-foreground mb-2">Overall Score</h4>
                        <div className="text-4xl font-heading font-bold">
                          {results.performancePrediction.overall_score}
                        </div>
                        <Progress value={results.performancePrediction.overall_score} className="h-2 mt-2" />
                      </div>
                    </div>

                    <div className="dashboard-card">
                      <h4 className="font-semibold mb-3">Recommendations</h4>
                      <ul className="space-y-2">
                        {results.performancePrediction.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>
    </div>
  );
}
